<script lang="ts" setup>
import { onMounted, onUnmounted, ref, watch, computed } from 'vue';
import { VueCookieNext } from 'vue-cookie-next';

import SidebarItem from './components/SidebarItem.vue';
import MobileSidemenuButton from './components/MobileSidemenuButton.vue';
import { PERMISSIONS } from '@/enum';
import { can, useDateTime } from '@/composables';
import { useAuthStore } from '@/store';

const collapseMenu = ref(false);

const authStore = useAuthStore();
const itemsShowMenu = ref(false);
const dateUtilities = useDateTime();

onMounted(() => {
  if (localStorage.collapseMenu) {
    collapseMenu.value = localStorage.collapseMenu === 'true' ? true : false;
  }
  itemsMenu();
});

const setDatedRoute = (name: string) => {
  const start = dateUtilities.formatDateForApi(dateUtilities.baseMonth());
  const end = dateUtilities.convertInvoiceDateToNextMonth(dateUtilities.baseMonth() as Date, 1, 0, false);
  return `${name}?start_at=${start}&end_at=${end}`;
};

const items = (() => {
  return [
    {
      name: 'Dashboard',
      route: '/',
      icon: 'home',
      style: { width: '18px' },
      permissions: [PERMISSIONS.MENU_DASHBOARD_VIEW],
    },
    {
      name: 'Calendar',
      route: '/calendar',
      icon: 'event',
      style: { width: '18px' },
      permissions: [PERMISSIONS.MENU_CALENDAR_VIEW],
    },
    {
      name: 'Billing',
      route: setDatedRoute('/billing'),
      icon: 'attach_money',
      style: { width: '18px' },
      permissions: [PERMISSIONS.MENU_BILLING_VIEW],
    },
    {
      name: 'Users',
      route: '/users',
      icon: 'supervisor_account',
      style: { width: '18px' },
      permissions: [PERMISSIONS.MENU_USERS_VIEW],
    },
    {
      name: 'Organizations',
      route: '/organizations',
      icon: 'domain',
      style: { width: '16px' },
      permissions: [PERMISSIONS.MENU_ORGANIZATIONS_VIEW],
    },
    {
      name: 'Reports',
      route: setDatedRoute('/reports'),
      icon: 'bar_chart',
      style: { width: '14px' },
      permissions: [PERMISSIONS.MENU_REPORTS_VIEW],
    },
  ];
})();

watch(
  () => authStore.permissionGlobal,
  () => {
    itemsMenu();
  },
);

const tenantGuest = ref(false);

const itemsMenu = () => {
  tenantGuest.value = VueCookieNext.getCookie('access_token_tenant');
  itemsShowMenu.value = items.filter((item) => {
    if (tenantGuest.value && tenantGuest.value.guest_tenant.master !== 1)
      return can(item.permissions || []) && item.name !== 'Billing';
    else return can(item.permissions || []);
  });
};

const toggleCollapse = () => {
  collapseMenu.value = !collapseMenu.value;
  localStorage.collapseMenu = collapseMenu.value ? 'true' : 'false';
};

const displayMobileMenu = ref(false);

const toggleMobileMenu = () => {
  displayMobileMenu.value = !displayMobileMenu.value;
  window.addEventListener('resize', checkWindowWidth);
};

const checkWindowWidth = () => {
  if (window.innerWidth > 480) {
    displayMobileMenu.value = false;
    window.removeEventListener('resize', checkWindowWidth);
  }
};

const hasTenantGuest = computed(() => !!tenantGuest.value);

onUnmounted(() => window.removeEventListener('resize', checkWindowWidth));
</script>

<template>
  <MobileSidemenuButton :viewing-tenant="hasTenantGuest" @click="toggleMobileMenu" />
  <div
    :class="{
      collapsed: collapseMenu && !displayMobileMenu,
      banner: authStore.isSwitchTenant,
      mobile: displayMobileMenu,
    }"
    class="sidebar"
  >
    <div v-if="itemsShowMenu && itemsShowMenu.length">
      <SidebarItem
        v-for="item in itemsShowMenu"
        :key="item.icon"
        :name="item.name"
        :icon="item.icon"
        :mobile="displayMobileMenu"
        :route="item.route"
        :style="item.style"
        :collapse="collapseMenu && !displayMobileMenu"
      />
    </div>
    <div v-else>
      <Skeleton width="100%" height="2rem" border-radius="2rem" class="mb-2" />
      <Skeleton width="100%" height="2rem" border-radius="2rem" class="mb-2" />
      <Skeleton width="100%" height="2rem" border-radius="2rem" class="mb-2" />
      <Skeleton width="100%" height="2rem" border-radius="2rem" class="mb-2" />
      <Skeleton width="100%" height="2rem" border-radius="2rem" class="mb-2" />
    </div>
    <Button class="collapse-button" @click="toggleCollapse">
      <span class="material-icons collapse-button__chevron-icon_left">chevron_left</span>
      <span class="material-icons collapse-button__chevron-icon_right">chevron_right</span>
      <p>Collapse</p>
    </Button>
  </div>
</template>

<style lang="scss" scoped>
.sidebar {
  height: calc(100vh - 56px);
  flex-direction: column;
  position: sticky;
  left: 0px;
  top: 3.5rem;
  max-width: 180px;
  display: flex;
  padding: 0 0.5rem;

  &.banner {
    height: calc(100vh - 88px);
    top: 5.5rem;
  }

  &.collapsed {
    max-width: unset;
    min-width: unset;
    width: 48px;
    .collapse-button {
      p {
        display: none;
      }
      .collapse-button__chevron-icon_left {
        display: none;
      }
      .collapse-button__chevron-icon_right {
        display: block;
      }
    }
  }

  &.mobile {
    @media only screen and (max-width: 480px) {
      display: block;
      width: 180px;
      z-index: 10;
      position: fixed;
      padding: 0.5rem 0.5rem 0rem 0.5rem;
      top: 48px;
      left: 0.5rem;
      height: fit-content;
      background: white;
      border-radius: 1.5rem;
      box-shadow: 0px 0.5rem 1rem rgba(0, 0, 0, 0.12);
      &.banner {
        top: 80px;
      }
    }
  }

  @media (max-width: 768px) {
    width: 48px;
  }

  @media only screen and (max-width: 480px) {
    display: none;
  }
  .collapse-button {
    font-weight: 300;
    position: absolute;
    left: 0px;
    bottom: 0px;
    text-decoration: none;
    padding: 0.5rem;
    width: 100%;
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: flex-start;
    background-color: white;
    border: none;
    color: $grayDark;

    &:focus,
    &:enabled:hover {
      background: white;
      border-color: white;
      box-shadow: none;
    }

    .material-icons {
      color: var(--primary-color-medium);
      font-size: 1rem;
      padding: 0.5rem;
    }
    .collapse-button__chevron-icon_right {
      display: none;
    }

    @media (max-width: 768px) {
      display: none;
    }
  }
}
</style>
